import { z } from "zod";

const safeText = z
  .string()
  .trim()
  .min(1)
  .max(120)
  .refine((value) => !/[<>]/.test(value), "Remove angle brackets.");

export const createGroupSchema = z.object({
  name: safeText.min(2, "Use at least 2 characters."),
  description: z.string().trim().max(500).optional(),
  icon: z.string().trim().min(1).max(8),
  visibility: z.enum(["private", "invite_only"]),
});

export const updateGroupSchema = createGroupSchema.pick({
  name: true,
  description: true,
  icon: true,
});

export const createPageSchema = z.object({
  title: safeText.default("Untitled"),
  parentPageId: z.string().min(1).nullable(),
});

export const renamePageSchema = z.object({ title: safeText });

export const invitationSchema = z.object({
  email: z
    .email("Enter a valid email address.")
    .transform((email) => email.trim().toLocaleLowerCase()),
  role: z.enum(["admin", "editor", "viewer"]),
});

export const roleUpdateSchema = z.object({
  userId: z.string().min(1),
  role: z.enum(["admin", "editor", "viewer"]),
});

export const pagePermissionSchema = z.object({
  strategy: z.enum([
    "inherit_group",
    "allow_selected",
    "deny_selected",
    "owner_admin_only",
  ]),
  memberOverrides: z.array(
    z.object({
      userId: z.string().min(1),
      access: z.enum(["none", "view", "edit"]),
    }),
  ),
  inheritToChildren: z.boolean(),
});

export const profileSchema = z.object({
  displayName: safeText.min(2),
});

export const attachmentSchema = z.object({
  name: z.string().trim().min(1).max(180),
  size: z.number().positive(),
  type: z.string().min(1),
});

export type CreateGroupInput = z.infer<typeof createGroupSchema>;
export type CreatePageInput = z.infer<typeof createPageSchema>;
export type InvitationInput = z.infer<typeof invitationSchema>;
export type PagePermissionInput = z.infer<typeof pagePermissionSchema>;
